// Function index:
//   mountStrategicMap(partyId, ws?, isPaused?) — wire the Map button +
//       overlay; lazy-fetches and renders the overworld view on first
//       open and on every subsequent open (data is cheap and might be
//       stale across moves / discoveries). ws enables click-to-fast-
//       travel; isPaused() blocks travel during a rules pause.
//
// NM-G2 of the nested-maps plan: an overworld-wide view available even
// from inside an interior. SVG-rendered. Discovered hexes only —
// undiscovered territory stays dark. Phase 6: when the party is standing
// on the overworld (not anchored inside an interior) and a socket is
// available, discovered hexes are click-to-fast-travel.

import { fetchOverworldMap, getQuests } from './api/parties.js';
import type { StrategicMapResponse } from './api/types.js';
import { questWaypoints, type QuestWaypoint } from './strategic-map/waypoints.js';
import type { WS } from './ws.js';

// Pointy-top hex math. HEX_R is the centre-to-vertex distance; one
// hex spans sqrt(3)*HEX_R wide and 2*HEX_R tall, with rows offset by
// 1.5*HEX_R vertically. HEX_R=6 fits a 100x100 grid comfortably in the
// 1200x800 panel with scroll headroom.
const HEX_R = 6;
const SQRT3 = Math.sqrt(3);
const HEX_W = SQRT3 * HEX_R;
const HEX_H = 2 * HEX_R;
const ROW_H = 1.5 * HEX_R;

// Same colours as the in-scene tile palette so the map reads as a
// shrunken version of the overworld. Unknown terrain falls back to
// dim grey (server only returns known hexes, but defensive).
const TERRAIN_COLOUR: Record<string, string> = {
  grass:    '#4a7a3a',
  forest:   '#2d5a2d',
  hill:     '#7a6b3a',
  mountain: '#6a6a6a',
  water:    '#2a5a8a',
  desert:   '#b8a06a',
};

const LOCATION_COLOUR: Record<string, string> = {
  cave:      '#4a4a4a',
  tower:     '#88c0ff',
  labyrinth: '#a888ff',
  dungeon:   '#c04848',
  city:      '#f5c468',
  town:      '#e0a848',
  village:   '#c88838',
  hamlet:    '#a87028',
  castle:    '#c8c8d0',
  monastery: '#ddddc8',
  ruin:      '#886848',
};

export function mountStrategicMap(partyId: number | string, ws?: WS, isPaused?: () => boolean): void {
  const btn = document.getElementById('map-btn') as HTMLButtonElement;
  const overlay = document.getElementById('map-overlay') as HTMLDivElement;
  const close = document.getElementById('map-close') as HTMLButtonElement;
  const canvas = document.getElementById('map-canvas') as HTMLDivElement;
  const where = document.getElementById('map-where') as HTMLSpanElement;

  // Travel is allowed only when a socket is wired AND the party is standing on
  // the overworld (not anchored at an interior doorway). Tracked across opens.
  let canTravel = false;

  const open = async (): Promise<void> => {
    overlay.hidden = false;
    canvas.innerHTML = '<div style="padding: 20px; color: #888; font-style: italic;">loading…</div>';
    where.textContent = '';
    try {
      const data = await fetchOverworldMap(partyId);
      canTravel = !!ws && !!data.anchor && !data.anchor.inside_name;
      // Quest givers are a second, cheap fetch rather than a change to the map
      // route — the map refetches on every open, so markers stay live without
      // new server surface.
      let waypoints: QuestWaypoint[] = [];
      try {
        const { quests } = await getQuests(partyId);
        waypoints = questWaypoints(quests, data.world.id);
      } catch { /* the map is still worth showing without markers */ }
      render(data, canvas, where, canTravel, waypoints);
    } catch (err) {
      canvas.innerHTML = `<div style="padding: 20px; color: #ff8888;">load failed: ${(err as Error).message}</div>`;
    }
  };
  const hide = (): void => { overlay.hidden = true; };

  btn.addEventListener('click', () => { void open(); });
  close.addEventListener('click', hide);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) hide(); });
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !overlay.hidden) hide();
  });

  // Delegated click-to-travel: read the target hex's coords off the polygon.
  if (ws) {
    canvas.addEventListener('click', (e) => {
      if (!canTravel || isPaused?.()) return;
      const el = (e.target as Element).closest('[data-travel-q]');
      if (!el) return;
      const q = Number(el.getAttribute('data-travel-q'));
      const r = Number(el.getAttribute('data-travel-r'));
      if (!Number.isInteger(q) || !Number.isInteger(r)) return;
      ws.send('player.travel', { q, r });
      hide();
    });
  }
}

function render(
  data: StrategicMapResponse,
  canvas: HTMLDivElement,
  where: HTMLSpanElement,
  canTravel: boolean,
  waypoints: QuestWaypoint[] = [],
): void {
  // "where" header tells the player whether they're standing on the
  // map or anchored inside an interior.
  if (data.anchor?.inside_name) {
    where.textContent = `Inside ${data.anchor.inside_name} (anchored at the doorway)`;
  } else if (data.anchor) {
    where.textContent = `You are at (${data.anchor.q}, ${data.anchor.r})${canTravel ? ' · click a hex to travel' : ''}`;
  } else {
    where.textContent = data.world.name;
  }

  if (data.hexes.length === 0) {
    canvas.innerHTML = '<div style="padding: 20px; color: #888; font-style: italic;">No territory discovered yet — walk to reveal the world.</div>';
    return;
  }

  // Frame the SVG around the actual discovered region with a margin so
  // a player who's only walked a few hexes isn't forced to scroll
  // through 100x100 of black.
  let minQ = Infinity, maxQ = -Infinity, minR = Infinity, maxR = -Infinity;
  for (const h of data.hexes) {
    if (h.q < minQ) minQ = h.q;
    if (h.q > maxQ) maxQ = h.q;
    if (h.r < minR) minR = h.r;
    if (h.r > maxR) maxR = h.r;
  }
  const PAD = 4;
  // Convert q/r extents to pixel extents via axial→pixel.
  const corners = [
    axial(minQ, minR), axial(maxQ, minR), axial(minQ, maxR), axial(maxQ, maxR),
  ];
  const minX = Math.min(...corners.map((p) => p.x)) - HEX_W / 2 - PAD;
  const maxX = Math.max(...corners.map((p) => p.x)) + HEX_W / 2 + PAD;
  const minY = Math.min(...corners.map((p) => p.y)) - HEX_R - PAD;
  const maxY = Math.max(...corners.map((p) => p.y)) + HEX_R + PAD;
  const w = maxX - minX;
  const h = maxY - minY;

  const parts: string[] = [];
  parts.push(`<svg width="${w}" height="${h}" viewBox="${minX} ${minY} ${w} ${h}" xmlns="http://www.w3.org/2000/svg">`);

  // Hex polygons. drawn in document order; pointy-top vertices start
  // at "12 o'clock". When travel is enabled, each hex carries its coords
  // + a pointer cursor so the delegated click handler can fast-travel.
  for (const hex of data.hexes) {
    const { x, y } = axial(hex.q, hex.r);
    const travel = canTravel
      ? ` data-travel-q="${hex.q}" data-travel-r="${hex.r}" style="cursor:pointer"`
      : '';
    parts.push(hexPath(x, y, TERRAIN_COLOUR[hex.terrain] ?? '#222', travel));
  }

  // Roads — the server sends only segments whose BOTH hexes the party has
  // discovered, so fog of war holds. Drawn under the doorway markers.
  for (const road of data.roads) {
    for (const [[q1, r1], [q2, r2]] of road.segments) {
      const a = axial(q1, r1);
      const b = axial(q2, r2);
      parts.push(
        `<line x1="${a.x.toFixed(2)}" y1="${a.y.toFixed(2)}" x2="${b.x.toFixed(2)}" y2="${b.y.toFixed(2)}" ` +
        `stroke="#9a7b4f" stroke-width="1.6" stroke-linecap="round" />`,
      );
    }
  }

  // Doorway markers — small filled diamonds on top of the hex they sit
  // on. Same kind-tint as the in-scene icons so the map reads
  // consistently. NPCs / characters are not drawn here (this is a
  // strategic view).
  for (const loc of data.locations) {
    const { x, y } = axial(loc.q, loc.r);
    const colour = LOCATION_COLOUR[loc.kind] ?? '#aaa';
    parts.push(
      `<rect x="${x - 2.5}" y="${y - 2.5}" width="5" height="5" transform="rotate(45 ${x} ${y})" fill="${colour}" stroke="#000" stroke-width="0.5">`,
      `<title>${escapeXml(loc.kind)} — ${escapeXml(loc.name)}${loc.interior_world_id ? '' : ' (interior not yet mapped)'}</title>`,
      `</rect>`,
    );
  }

  // Quest givers, drawn under the anchor so the player's own position always
  // reads first. A `ready` quest gets a FILLED marker — that one wants action;
  // an active quest's giver is only context. The map renders discovered hexes
  // only, so a giver still out in the dark floats over black: that is the
  // feature, and the tooltip names who is out there.
  for (const w of waypoints) {
    const { x, y } = axial(w.q, w.r);
    const colour = w.ready ? '#ffd24a' : '#c9a227';
    parts.push(
      `<path d="M ${x} ${(y - HEX_R * 0.9).toFixed(2)} L ${(x + HEX_R * 0.45).toFixed(2)} ${(y - HEX_R * 0.2).toFixed(2)} `
      + `L ${(x - HEX_R * 0.45).toFixed(2)} ${(y - HEX_R * 0.2).toFixed(2)} Z" `
      + `fill="${w.ready ? colour : 'none'}" stroke="${colour}" stroke-width="1.2">`,
      `<title>${escapeXml(w.giver)} — ${escapeXml(w.title)}${w.ready ? ' (ready to hand in)' : ''}</title>`,
      `</path>`,
    );
  }

  // Anchor: where the player is right now. Hollow gold ring; the
  // interior-name caption in the header explains the "anchored at the
  // doorway" case when they're inside.
  if (data.anchor) {
    const { x, y } = axial(data.anchor.q, data.anchor.r);
    parts.push(
      `<circle cx="${x}" cy="${y}" r="${HEX_R * 0.55}" fill="none" stroke="#ffeb88" stroke-width="1.5" />`,
      `<circle cx="${x}" cy="${y}" r="${HEX_R * 0.18}" fill="#ffeb88" />`,
    );
  }

  parts.push('</svg>');
  canvas.innerHTML = parts.join('');
}

function axial(q: number, r: number): { x: number; y: number } {
  return {
    x: HEX_W * (q + r / 2),
    y: ROW_H * r,
  };
}

function hexPath(cx: number, cy: number, fill: string, extra = ''): string {
  // Pointy-top: vertices at angles 30°, 90°, 150°, 210°, 270°, 330°.
  // We precomputed sin/cos in axial(); just emit a points list.
  const pts: string[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i - 30);
    pts.push(`${(cx + HEX_R * Math.cos(a)).toFixed(2)},${(cy + HEX_R * Math.sin(a)).toFixed(2)}`);
  }
  return `<polygon points="${pts.join(' ')}" fill="${fill}" stroke="#0a0a0a" stroke-width="0.5"${extra} />`;
}

function escapeXml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    c === '&' ? '&amp;' :
    c === '<' ? '&lt;' :
    c === '>' ? '&gt;' :
    c === '"' ? '&quot;' : '&apos;'
  ));
}
// HEX_H referenced for breadth-of-row sanity, retained as a future
// hook if we ever switch to flat-top hexes.
void HEX_H;
